/**
 * 会话行列表区（14.36b 从 ConversationList 抽出）：主列表与归档页共用同一份
 * 行渲染 + 行菜单，口径只有一处。
 *
 * - 行定高 ROW_HEIGHT，由本文件与 conversationList.module.scss 共同保证
 *   （row 高度 100% 撑满 FixedList 的定位行，内容溢出截断）。
 * - 选中态：activeChatKey(nav) 推导（桌面双栏右栏开着的会话）；移动端栈顶是
 *   home 时无 chat 在上层可见，样式挂着也不可见，无需按端分支。
 * - 时间列每分钟整体重算一次（useNowMinute），避免「3 分钟前打开的页面还显示
 *   旧的 HH:mm 跨天不更新」。
 * - 行菜单（14.36）：PC 右键 / H5 长按（useLongPress 委托在本容器上，虚拟行
 *   频繁挂卸不逐行挂监听——MessageList 同口径），动作集见 archiveModel：
 *   归档态由 GlobalState.archive 判定，所以主列表出「归档」、归档页出「移出归档」，
 *   两处调用方都不必传。
 * - 草稿 bump（14.12）只在渲染层重排，不碰 store；无草稿时原引用直返零开销。
 * - 在线绿点（14.38）只对私聊行渲染，且只在 presence 明确在线时出现（条目缺失
 *   = 未知，不画灰点；口径与数据源见 chat/presenceModel 文件头）。
 */
import { useCallback, useState } from 'preact/hooks';
import { FixedList } from '~/list/FixedList';
import { useGlobal } from '~/store/useGlobal';
import { activeChatKey } from '~/nav/projection';
import { appRouter } from '~/nav/appRouter';
import { openChat } from '~/nav/actions';
import { conversationRowView, orderRowsWithDrafts } from './conversationRowModel';
import { avatarStyle } from '~/chat/avatarColor';
import { VipBadge } from './VipBadge';
import { conversationMenuActions, type ConversationMenuActionId } from './archiveModel';
import { MessageMenuLazy } from './MessageMenuLazy';
import { archiveConnection } from '~/store/appWorker';
import type { DraftEntry } from '~/store/draftsState';
import type { ConversationRecord } from '~/worker/contract';
import { peerUidOfKey, presenceOf } from '~/chat/presenceModel';
import { useNowMinute } from './useNowMinute';
import { useLongPress } from './useLongPress';
import styles from './conversationList.module.scss';
import { IconCheck, IconCheckAll, IconClock, IconClose } from '~/ui/icons';

export const ROW_HEIGHT = 68;

export function ConversationRows(props: {
  rows: readonly ConversationRecord[];
  /** 空列表时的文案与 testId（主列表=「会话都在归档里」，归档页=「暂无归档会话」） */
  emptyText: string;
  emptyTestId: string;
}) {
  const drafts = useGlobal((s) => s.drafts);
  const archive = useGlobal((s) => s.archive);
  // 在线态（14.38）：整张 map 订阅在容器一层——行是虚拟化的、每帧只有十几行在屏，
  // 逐行订阅反而更贵；map 无净变化时推送不换引用（applyPresence 返回原引用），
  // 所以上下线才会触发这一层重渲染
  const presence = useGlobal((s) => s.presence);
  const cdn = useGlobal((s) => s.config?.cdn ?? '');
  const selectedKey = useGlobal((s) => activeChatKey(s.nav));
  const isMobile = useGlobal((s) => s.layout.isMobile);
  const nowMs = useNowMinute();
  // 自己的 uid（21v）：行尾发送态标记要判「末条是不是我发的」
  const myUid = useGlobal((st) => (st.auth.status === 'logged-in' ? st.auth.userId : 0));

  const [menu, setMenu] = useState<{ key: string; x: number; y: number } | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  // 委托取行：按下/右键落点 → 最近的会话行 → data-key
  const openMenuAt = useCallback((target: Element, x: number, y: number) => {
    const key = target.closest('[data-testid="conv-row"]')?.getAttribute('data-key');
    if (key != null && key !== '') setMenu({ key, x, y });
  }, []);
  const longPressRef = useLongPress(isMobile, (x, y, target) => openMenuAt(target, x, y));

  const onMenuAction = useCallback(
    (id: ConversationMenuActionId) => {
      const key = menu?.key;
      setMenu(null);
      if (key === undefined) return;
      archiveConnection().setArchived(key, id === 'archive');
    },
    [menu],
  );

  const ordered = orderRowsWithDrafts(props.rows, drafts);
  return (
    <div
      className={styles.listArea}
      ref={longPressRef}
      onContextMenu={(e) => {
        if (isMobile) return; // 长按路径；系统菜单已被 useLongPress 抑制
        if (!(e.target instanceof Element)) return;
        e.preventDefault();
        openMenuAt(e.target, e.clientX, e.clientY);
      }}
    >
      {ordered.length === 0 ? (
        <div className={styles.empty} data-testid={props.emptyTestId}>
          {props.emptyText}
        </div>
      ) : (
        <FixedList
          items={ordered}
          itemKey={(r) => r.key}
          itemHeight={ROW_HEIGHT}
          renderItem={(r) => (
            <ConversationRow
              record={r}
              cdn={cdn}
              nowMs={nowMs}
              myUid={myUid}
              selected={r.key === selectedKey}
              draft={drafts?.[r.key] ?? null}
              online={presenceOf(presence, peerUidOfKey(r.key)) === 'online'}
            />
          )}
        />
      )}
      {menu !== null && (
        <MessageMenuLazy
          actions={conversationMenuActions(archive?.has(menu.key) ?? false)}
          anchorX={menu.x}
          anchorY={menu.y}
          mobile={isMobile}
          testId="conv-menu"
          onAction={onMenuAction}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

function ConversationRow(props: {
  record: ConversationRecord;
  cdn: string;
  nowMs: number;
  /** 自己的 uid（21v）：行尾发送态标记要判「末条是不是我发的」 */
  myUid: number;
  selected: boolean;
  draft: DraftEntry | null;
  /** 对端在线（14.38；仅私聊可能为真，群聊/未知态恒 false） */
  online: boolean;
}) {
  const v = conversationRowView(props.record, props.cdn, props.nowMs, props.draft, props.myUid);
  const className = [
    styles.row,
    v.pinned ? styles.pinnedRow : '',
    props.selected ? styles.selectedRow : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type="button"
      className={className}
      data-testid="conv-row"
      data-key={v.key}
      data-selected={props.selected ? 'true' : undefined}
      onClick={() => openChat(appRouter(), v.key)}
    >
      <span className={styles.avatarWrap}>
        <span className={styles.avatar} aria-hidden="true" style={avatarStyle(v.name)}>
          <span className={styles.avatarChar}>{v.avatarChar}</span>
          {v.avatarUrl && (
            <img
              className={styles.avatarImg}
              src={v.avatarUrl}
              alt=""
              loading="lazy"
              onError={(e) => {
                // 加载失败隐藏，露出底下的首字占位（不引入每行 error state）
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
        </span>
        {props.online && (
          <span className={styles.onlineDot} data-testid="conv-online" title="在线" />
        )}
      </span>
      <span className={styles.main}>
        <span className={styles.topLine}>
          <span className={styles.nameBox}>
            <span className={styles.name}>{v.name}</span>
            <VipBadge view={v.vip} />
          </span>
          {/* 末条是我发的时显示发送态/已读（21v，旧版同位：标记在时间左边） */}
          {v.sendStatus !== null && (
            <span
              className={styles.sendMark}
              data-status={v.sendStatus}
              data-testid="conv-send-status"
              aria-hidden="true"
            >
              {v.sendStatus === 'pending' ? (
                <IconClock class={styles.sendMarkIcon} />
              ) : v.sendStatus === 'failed' ? (
                <IconClose class={styles.sendMarkIcon} />
              ) : v.sendStatus === 'read' ? (
                <IconCheckAll class={styles.sendMarkIcon} />
              ) : (
                <IconCheck class={styles.sendMarkIcon} />
              )}
            </span>
          )}
          <span className={styles.time} data-testid="conv-time">
            {v.time}
          </span>
        </span>
        <span className={styles.bottomLine}>
          <span className={styles.preview}>
            {v.mentioned && <span className={styles.atMark}>[有人@我]</span>}
            {v.draft && (
              <span className={styles.draftMark} data-testid="conv-draft">
                [草稿]
              </span>
            )}
            {v.preview}
          </span>
          {v.badge && (
            <span
              className={v.badge.muted ? styles.badgeMuted : styles.badge}
              data-testid="conv-badge"
              data-muted={v.badge.muted ? 'true' : undefined}
            >
              {v.badge.text}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
